'use client';

import { useState, useEffect, useCallback } from 'react';
import { Star, Quote, Plus, X, CheckCircle, ChevronLeft, ChevronRight, Loader2, TrendingUp, Award, Users } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Testimonial {
  id: string;
  farmerName: string;
  location: string;
  cropType: string;
  story: string;
  treatmentOutcome: string | null;
  appRating: number;
  createdAt: string;
}

const CROPS = ['ধান', 'গম', 'আলু', 'টমেটো', 'মরিচ', 'বেগুন', 'সরিষা', 'পাট', 'ভুট্টা', 'সবজি', 'অন্যান্য'];

const AVATAR_COLORS = [
  'bg-green-600', 'bg-amber-600', 'bg-blue-600', 'bg-purple-600',
  'bg-rose-600', 'bg-teal-600', 'bg-orange-600', 'bg-indigo-600',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return parts[0].charAt(0) + parts[1].charAt(0);
  return name.substring(0, 2);
}

function StarRating({ value, onChange, readonly = false }: { value: number; onChange?: (v: number) => void; readonly?: boolean }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={readonly ? 'button' : 'button'}
          disabled={readonly}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          className={readonly ? 'cursor-default' : 'cursor-pointer transition-transform hover:scale-110'}
          aria-label={`${star} তারা`}
        >
          <Star
            size={readonly ? 14 : 22}
            className={
              star <= (hovered || value)
                ? 'text-amber-400 fill-amber-400' :'text-gray-300 fill-gray-100'
            }
          />
        </button>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [form, setForm] = useState({
    farmerName: '',
    location: '',
    cropType: 'ধান',
    story: '',
    treatmentOutcome: '',
    appRating: 5,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const CARDS_PER_PAGE = typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 3;
  const [cardsPerPage, setCardsPerPage] = useState(3);

  useEffect(() => {
    const update = () => setCardsPerPage(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const totalPages = Math.ceil(testimonials.length / cardsPerPage);

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data, error, count } = await supabase
        .from('community_testimonials')
        .select('*', { count: 'exact' })
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(30);

      if (error) {
        console.log('Fetch testimonials error:', error.message);
        setLoading(false);
        return;
      }

      const mapped: Testimonial[] = (data || []).map((row: any) => ({
        id: row.id,
        farmerName: row.farmer_name,
        location: row.location,
        cropType: row.crop_type,
        story: row.story,
        treatmentOutcome: row.treatment_outcome,
        appRating: row.app_rating,
        createdAt: row.created_at,
      }));

      setTestimonials(mapped);
      setTotalCount(count || 0);

      if (mapped.length > 0) {
        const avg = mapped.reduce((sum, t) => sum + t.appRating, 0) / mapped.length;
        setAvgRating(Math.round(avg * 10) / 10);
      }
    } catch (err) {
      console.log('Testimonials fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.farmerName.trim()) newErrors.farmerName = 'নাম প্রয়োজন';
    if (!form.location.trim()) newErrors.location = 'এলাকা প্রয়োজন';
    if (!form.story.trim() || form.story.trim().length < 30) newErrors.story = 'অন্তত ৩০ অক্ষরের গল্প লিখুন';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase.from('community_testimonials').insert({
        user_id: user?.id || null,
        farmer_name: form.farmerName.trim(),
        location: form.location.trim(),
        crop_type: form.cropType,
        story: form.story.trim(),
        treatment_outcome: form.treatmentOutcome.trim() || null,
        app_rating: form.appRating,
        is_approved: false,
      });

      if (error) {
        console.log('Submit testimonial error:', error.message);
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setForm({ farmerName: '', location: '', cropType: 'ধান', story: '', treatmentOutcome: '', appRating: 5 });
      setTimeout(() => {
        setSubmitted(false);
        setShowForm(false);
      }, 3500);
    } catch (err) {
      console.log('Submit failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const visibleTestimonials = testimonials.slice(
    currentPage * cardsPerPage,
    currentPage * cardsPerPage + cardsPerPage
  );

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: testimonials.filter((t) => t.appRating === star).length,
    pct: testimonials.length > 0
      ? Math.round((testimonials.filter((t) => t.appRating === star).length / testimonials.length) * 100)
      : 0,
  }));

  return (
    <section className="py-14 sm:py-20 bg-gradient-to-b from-green-50 to-white overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <p className="section-label mb-3">কৃষকদের সাফল্যের গল্প</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            কমিউনিটি রিভিউ ও রেটিং
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            সারা বাংলাদেশের কৃষকরা তাদের সাফল্যের গল্প ও চিকিৎসার ফলাফল শেয়ার করছেন
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-10 sm:mb-14">
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-green-100 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Users size={18} className="text-green-600" />
              <span className="text-xl sm:text-3xl font-bold text-gray-900">{totalCount > 0 ? `${totalCount}+` : '১২,০০০+'}</span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500">সক্রিয় কৃষক</p>
          </div>
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-amber-100 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Star size={18} className="text-amber-500 fill-amber-500" />
              <span className="text-xl sm:text-3xl font-bold text-gray-900">{avgRating > 0 ? avgRating : '4.8'}</span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500">গড় রেটিং</p>
          </div>
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-blue-100 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <TrendingUp size={18} className="text-blue-600" />
              <span className="text-xl sm:text-3xl font-bold text-gray-900">৯৫%</span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500">সন্তুষ্টি হার</p>
          </div>
        </div>

        {/* Rating Distribution + Submit Button */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-10 items-start">
          {/* Rating Bars */}
          <div className="w-full sm:w-72 bg-white rounded-2xl p-5 shadow-sm border border-gray-100 shrink-0">
            <div className="flex items-center gap-2 mb-4">
              <Award size={18} className="text-amber-500" />
              <h3 className="font-semibold text-gray-800 text-sm">রেটিং বিতরণ</h3>
            </div>
            <div className="space-y-2">
              {ratingDistribution.map(({ star, count, pct }) => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-4 shrink-0">{star}</span>
                  <Star size={11} className="text-amber-400 fill-amber-400 shrink-0" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right shrink-0">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Card */}
          <div className="flex-1 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 sm:p-8 text-white flex flex-col justify-between min-h-[160px]">
            <div>
              <h3 className="text-lg sm:text-xl font-bold mb-2">আপনার সাফল্যের গল্প শেয়ার করুন</h3>
              <p className="text-green-100 text-sm sm:text-base">
                আপনার অভিজ্ঞতা অন্য কৃষকদের অনুপ্রাণিত করবে। রোগ চিকিৎসার ফলাফল ও অ্যাপ রেটিং দিন।
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center gap-2 bg-white text-green-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-green-50 transition-all duration-200 active:scale-95 w-fit text-sm sm:text-base"
            >
              <Plus size={18} />
              রিভিউ লিখুন
            </button>
          </div>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 animate-pulse">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <div key={s} className="w-4 h-4 bg-gray-200 rounded" />)}
                </div>
                <div className="space-y-2 mb-5">
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-5/6" />
                  <div className="h-3 bg-gray-200 rounded w-4/6" />
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                  <div className="space-y-1.5">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-2.5 bg-gray-200 rounded w-32" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
            <Quote size={40} className="text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 text-base">এখনো কোনো রিভিউ নেই। প্রথম রিভিউ লিখুন!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 stagger-children">
              {visibleTestimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative group flex flex-col"
                >
                  <Quote size={20} className="text-green-100 absolute top-4 right-4 group-hover:text-green-200 transition-colors" />

                  {/* Stars */}
                  <div className="mb-3">
                    <StarRating value={t.appRating} readonly />
                  </div>

                  {/* Story */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-4 flex-1">{t.story}</p>

                  {/* Treatment Outcome Badge */}
                  {t.treatmentOutcome && (
                    <div className="flex items-start gap-1.5 bg-green-50 rounded-lg px-3 py-2 mb-4">
                      <CheckCircle size={14} className="text-green-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-green-700 leading-snug line-clamp-2">{t.treatmentOutcome}</p>
                    </div>
                  )}

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100 mt-auto">
                    <div className={`w-10 h-10 rounded-full ${getAvatarColor(t.farmerName)} flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm`}>
                      {getInitials(t.farmerName)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{t.farmerName}</p>
                      <p className="text-xs text-gray-400 truncate">{t.cropType} চাষী · {t.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-green-50 hover:border-green-300 hover:text-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  aria-label="আগের পাতা"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="flex gap-1.5">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${i === currentPage ? 'bg-green-600 w-6' : 'bg-gray-300 hover:bg-gray-400'}`}
                      aria-label={`পাতা ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-green-50 hover:border-green-300 hover:text-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  aria-label="পরের পাতা"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Submit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !submitting && setShowForm(false)}
          />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">আপনার অভিজ্ঞতা শেয়ার করুন</h3>
              <button
                onClick={() => !submitting && setShowForm(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                aria-label="বন্ধ করুন"
              >
                <X size={16} />
              </button>
            </div>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">ধন্যবাদ!</h4>
                <p className="text-gray-500 text-sm">আপনার রিভিউ সফলভাবে জমা হয়েছে। অনুমোদনের পর প্রকাশিত হবে।</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* App Rating */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    অ্যাপ রেটিং <span className="text-red-500">*</span>
                  </label>
                  <StarRating
                    value={form.appRating}
                    onChange={(v) => setForm((f) => ({ ...f, appRating: v }))}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    আপনার নাম <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.farmerName}
                    onChange={(e) => setForm((f) => ({ ...f, farmerName: e.target.value }))}
                    placeholder="যেমন: মোঃ রফিকুল ইসলাম"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.farmerName ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                  />
                  {errors.farmerName && <p className="text-xs text-red-500 mt-1">{errors.farmerName}</p>}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    এলাকা <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder="যেমন: নওগাঁ, রাজশাহী"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${errors.location ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                  />
                  {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
                </div>

                {/* Crop Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">ফসলের ধরন</label>
                  <select
                    value={form.cropType}
                    onChange={(e) => setForm((f) => ({ ...f, cropType: e.target.value }))}
                    className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                  >
                    {CROPS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Story */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    আপনার সাফল্যের গল্প <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={form.story}
                    onChange={(e) => setForm((f) => ({ ...f, story: e.target.value }))}
                    placeholder="AgroGuard AI কীভাবে আপনার ফসল রক্ষায় সাহায্য করেছে তা লিখুন..."
                    rows={4}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all resize-none ${errors.story ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.story ? <p className="text-xs text-red-500">{errors.story}</p> : <span />}
                    <span className="text-xs text-gray-400">{form.story.length} অক্ষর</span>
                  </div>
                </div>

                {/* Treatment Outcome */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    চিকিৎসার ফলাফল <span className="text-gray-400 font-normal">(ঐচ্ছিক)</span>
                  </label>
                  <input
                    type="text"
                    value={form.treatmentOutcome}
                    onChange={(e) => setForm((f) => ({ ...f, treatmentOutcome: e.target.value }))}
                    placeholder="যেমন: রোগ সম্পূর্ণ নিরাময় হয়েছে, ফলন ৩০% বৃদ্ধি পেয়েছে"
                    className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 text-sm sm:text-base mt-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      জমা হচ্ছে...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      রিভিউ জমা দিন
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 text-center">রিভিউ অনুমোদনের পর প্রকাশিত হবে</p>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}